import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { Annotation } from '../../amplify/appsync/query';
import { PatientVisit } from '../../amplify/appsync/search-patients';
import { DELETE_USER_ANNOTATION, INSERT_USER_ANNOTATION, UPDATE_USER_ANNOTATION, callWrite } from '../../amplify/appsync/write';

interface UserAnnotationModalProps {
    show: boolean,
    setShow: any,
    userAnnotationHighlightedTime: number | null,
    userAnnotations: Annotation[],
    setUserAnnotations: any,
    fetchChartAndAnnotations: any,
    patientVisit: PatientVisit,
}

function UserAnnotationModal({
    show,
    setShow,
    userAnnotationHighlightedTime,
    userAnnotations,
    setUserAnnotations,
    fetchChartAndAnnotations,
    patientVisit,
}: UserAnnotationModalProps) {
    const [submitting, setSubmitting] = useState(false);
    const [description, setDescription] = useState("");
    const [duration, setDuration] = useState(60);
    const [isExisting, setIsExisting] = useState(false);

    const handleClose = () => setShow(false);

    useEffect(() => {
        for (let annotation of userAnnotations) {
            if (annotation.annotation_time === userAnnotationHighlightedTime) {
                setDuration(annotation.annotation_length_seconds);
                setDescription(annotation.measure_name);
                setIsExisting(true);
                return;
            }
        }
        setDuration(60);
        setDescription("");
        setIsExisting(false);
    }, [show]);

    const save = () => {
        setSubmitting(true);
        
        if (userAnnotationHighlightedTime) {
            if (userAnnotations.filter(annotation => annotation.annotation_time === userAnnotationHighlightedTime).length > 0) {

                callWrite({
                    operation: UPDATE_USER_ANNOTATION,
                    annotation_id: userAnnotations.filter(annotation => annotation.annotation_time === userAnnotationHighlightedTime)[0].annotation_id,
                    csn: patientVisit.csn,
                    mrn: patientVisit.mrn,
                    annotation_time: userAnnotationHighlightedTime * 1000,
                    annotation_length_seconds: Math.max(60, duration),
                    measure_name: description,
                })
                .then((output: any) => {
                    setSubmitting(false);
                    let currAnnotation: Annotation = userAnnotations.filter(annotation => annotation.annotation_time === userAnnotationHighlightedTime)[0];
                    currAnnotation = {
                        ...currAnnotation,
                        annotation_length_seconds: duration,
                        measure_name: description,
                    }
                    setUserAnnotations([
                        ...userAnnotations.filter(annotation => annotation.annotation_time !== userAnnotationHighlightedTime),
                        currAnnotation,
                    ])
                    fetchChartAndAnnotations(patientVisit);
                    setShow(false);
                });
            } else {
                callWrite({
                    operation: INSERT_USER_ANNOTATION,
                    csn: patientVisit.csn,
                    mrn: patientVisit.mrn,
                    annotation_time: userAnnotationHighlightedTime * 1000,
                    annotation_length_seconds: Math.max(60, duration),
                    measure_name: description,
                })
                .then((output: any) => {
                    setSubmitting(false);
                    const currAnnotation: Annotation = output[0];
                    setUserAnnotations([
                        ...userAnnotations.filter(annotation => annotation.annotation_time !== userAnnotationHighlightedTime),
                        {
                            ...currAnnotation,
                        }
                    ])
                    fetchChartAndAnnotations(patientVisit);
                    setShow(false);
                });
            }
        }
    };

    const deleteAnnotation = () => {
        if (isExisting && userAnnotationHighlightedTime) {
            if (userAnnotations.filter(annotation => annotation.annotation_time === userAnnotationHighlightedTime).length > 0) {
                callWrite({
                    operation: DELETE_USER_ANNOTATION,
                    annotation_id: userAnnotations.filter(annotation => annotation.annotation_time === userAnnotationHighlightedTime)[0].annotation_id,
                })
                .then((output: any) => {
                    setUserAnnotations([
                        ...userAnnotations.filter(annotation => annotation.annotation_time !== userAnnotationHighlightedTime),
                    ])
                    fetchChartAndAnnotations(patientVisit);
                    setShow(false);
                });
            }
        }
    }

    return (
        <>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>User Annotation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {
                            userAnnotationHighlightedTime && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Annotate Time: <strong>{new Date(userAnnotationHighlightedTime * 1000).toLocaleDateString()} {new Date(userAnnotationHighlightedTime * 1000).toLocaleTimeString()}</strong></Form.Label>
                                </Form.Group>
                            )
                        }
                        <Form.Group className="mb-3">
                            <Form.Label>Annotation Duration (seconds)</Form.Label>
                            <Form.Control as="input" type="number" onChange={(event) => {setDuration(event.target.value as any)}} value={duration} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Annotation Description</Form.Label>
                            <Form.Control as="textarea" rows={3} onChange={(event) => {setDescription(event.target.value)}} value={description} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    {
                        isExisting && (
                            <Button variant="outline-secondary" onClick={deleteAnnotation}>
                                Delete
                            </Button>
                        )
                    }
                    <Button variant="primary" onClick={() => save()} disabled={submitting}>
                        {
                            submitting ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                                <>Save</>
                            )
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default UserAnnotationModal;
